import PropTypes from 'prop-types';
import React, { Fragment, useEffect } from 'react';
import injectSheet from 'react-jss';
import { Link } from 'react-router-dom-v5-compat';
import {
  useBlockLayout,
  useResizeColumns,
  useRowSelect,
  useRowState,
  useTable,
} from 'react-table';

import ActionsMenu from '~/components/Shared/ActionsMenu/ActionsMenu';
import EcCheckbox from '~/components/Shared/EcCheckbox/EcCheckbox';
import {
  getTableColumnWidthName,
  persistTableColumnWidth,
} from '~/components/Shared/ReactTable/ReactTable.utils';
import { Button } from '~/eds/src/components/Button/Button';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Tooltip } from '~/eds/src/components/Tooltip/Tooltip';
import { useIntl } from 'react-intl';
import { getStorageItem } from '~/utils/browser';

import { black3 } from '../../../assets/shared-styles/general';
import {
  MODAL_DELETE,
  MODAL_DOCUMENT_GROUP,
  MODAL_DOCUMENTS_COLUMN_FIELDS,
  MODAL_DOCUMENTS_COLUMN_FIELDS_SAVE,
  MODAL_DOCUMENTS_COLUMN_FIELDS_VIEW_SWITCH,
  MODAL_FOLDER_TREE,
  MODAL_MULTI_EDIT,
} from '../../../types/modal.types';
import { documentTypeIcon } from '../../DocumentsPage/Document.utils';
import FolderIcon from '../../Shared/Icons/FolderIcon';
import FolderLockIcon from '../../Shared/Icons/FolderLockIcon';
import FolderViewIcon from '../../Shared/Icons/FolderViewIcon';
import TreeIcon from '../../Shared/Icons/TreeIcon';
import styles from './DocumentsTable.styles';

function SelectAllMessage(props) {
  const intl = useIntl();
  const {
    classes,
    rows,
    folderItemsCount,
    selectedItems,
    selectedItemsList,
    handleOnSelectAllClick,
    isAllSelected,
  } = props;

  const isPageSelected = selectedItemsList.length === rows.length;
  const itemsCount = isAllSelected ? folderItemsCount : selectedItems.length;

  return (
    <div className={classes.selectAllMessage}>
      {intl.formatMessage(
        {
          id: 'DocumentsTable.itemsSelected',
          defaultMessage:
            '{count, plural, one {# item} other {# items}} selected',
          description:
            'Message showing number of selected items. Example: count = 5',
        },
        { count: itemsCount },
      )}
      {isPageSelected && !isAllSelected && folderItemsCount > itemsCount && (
        <button
          className={classes.selectAllContent}
          onClick={() => handleOnSelectAllClick(true)}
        >
          {intl.formatMessage(
            {
              id: 'DocumentsTable.selectAllItems',
              defaultMessage:
                'Select all {count, plural, one {# item} other {# items}}',
              description:
                'Button text to select all items in folder. Example: count = 10',
            },
            { count: folderItemsCount },
          )}
        </button>
      )}
    </div>
  );
}

function HeaderAddtoGroupAction(props) {
  const intl = useIntl();
  const { selectedItemsList, handleShowModal } = props;
  const hasFolderSelected = selectedItemsList.some(
    (item) => item.original.type === 'folder',
  );

  return (
    <Button
      disabled={hasFolderSelected}
      icon="groups"
      iconPosition="left"
      variant="action"
      text={intl.formatMessage({
        id: 'DocumentsTable.addToGroupButton',
        defaultMessage: 'Add to Group',
        description: 'Button text for adding documents to group',
      })}
      id="helpAddtoGroupTooltip"
      tooltip={
        hasFolderSelected
          ? intl.formatMessage({
              id: 'DocumentsTable.addToGroupFolderTooltip',
              defaultMessage:
                'To Add to Group, make sure your selection does not include folders',
              description: 'Tooltip when folders are selected for add to group',
            })
          : undefined
      }
      onClick={() => handleShowModal(MODAL_DOCUMENT_GROUP, selectedItemsList)}
    />
  );
}

function HeaderEditAction(props) {
  const intl = useIntl();
  const { selectedItemsList, handleShowModal } = props;

  const movableItems = selectedItemsList.filter(
    (item) => item.original.isMovable,
  );

  return (
    <Button
      disabled={selectedItemsList.length !== movableItems.length}
      icon="edit"
      iconPosition="left"
      variant="action"
      text={intl.formatMessage({
        id: 'DocumentsTable.editButton',
        defaultMessage: 'Edit',
        description: 'Button text for editing documents',
      })}
      id="helpEditTooltip"
      tooltip={
        selectedItemsList.length !== movableItems.length
          ? intl.formatMessage({
              id: 'DocumentsTable.editPrivilegeTooltip',
              defaultMessage:
                'You do not have privileges to edit one or more of the items you have selected.',
              description: 'Tooltip when user lacks edit privileges',
            })
          : undefined
      }
      onClick={() =>
        handleShowModal(MODAL_MULTI_EDIT, selectedItemsList, 'edit')
      }
    />
  );
}

function HeaderCopyAction(props) {
  const intl = useIntl();
  const { selectedItemsList, handleShowModal } = props;

  const movableItems = selectedItemsList.filter(
    (item) => item.original.isMovable,
  );

  return (
    <Button
      disabled={selectedItemsList.length !== movableItems.length}
      icon="clipboard"
      iconPosition="left"
      variant="action"
      text={intl.formatMessage({
        id: 'DocumentsTable.copyButton',
        defaultMessage: 'Copy',
        description: 'Button text for copying documents',
      })}
      id="helpCopyTooltip"
      tooltip={
        selectedItemsList.length !== movableItems.length
          ? intl.formatMessage({
              id: 'DocumentsTable.copyPrivilegeTooltip',
              defaultMessage:
                'You do not have privileges to copy one or more of the items you have selected.',
              description: 'Tooltip when user lacks copy privileges',
            })
          : undefined
      }
      onClick={() =>
        handleShowModal(MODAL_FOLDER_TREE, selectedItemsList, 'Copy')
      }
    />
  );
}

function HeaderMoveAction(props) {
  const intl = useIntl();
  const { selectedItemsList, handleShowModal } = props;
  const movableItems = selectedItemsList.filter(
    (item) => item.original.isMovable,
  );

  return (
    <Button
      disabled={selectedItemsList.length !== movableItems.length}
      icon="folder-move"
      iconPosition="left"
      variant="action"
      text={intl.formatMessage({
        id: 'DocumentsTable.moveButton',
        defaultMessage: 'Move',
        description: 'Button text for moving documents',
      })}
      id="helpMoveTooltip"
      tooltip={
        selectedItemsList.length !== movableItems.length
          ? intl.formatMessage({
              id: 'DocumentsTable.movePrivilegeTooltip',
              defaultMessage:
                'You do not have privileges to move one or more of the items you have selected.',
              description: 'Tooltip when user lacks move privileges',
            })
          : undefined
      }
      onClick={() =>
        handleShowModal(MODAL_FOLDER_TREE, selectedItemsList, 'Move')
      }
    />
  );
}

function HeaderDeleteAction(props) {
  const intl = useIntl();
  const { selectedItemsList, handleShowModal } = props;
  const movableItems = selectedItemsList.filter(
    (item) => item.original.isMovable,
  );
  const containSyncpairs = selectedItemsList.filter(
    (item) => item.original.contains_sync_pair,
  );

  const disabled =
    selectedItemsList.length !== movableItems.length ||
    selectedItemsList.length === containSyncpairs.length;

  const tooltip =
    selectedItemsList.length === containSyncpairs.length
      ? intl.formatMessage({
          id: 'DocumentsTable.deleteSyncPairTooltip',
          defaultMessage:
            'The selected item(s) contain synchronizations with external storage providers, please contact your Evisort admin to stop synchronization before deleting the selected item(s)',
          description: 'Tooltip when sync pair items are selected for deletion',
        })
      : selectedItemsList.length !== movableItems.length
        ? intl.formatMessage({
            id: 'DocumentsTable.deletePrivilegeTooltip',
            defaultMessage:
              'You do not have privileges to delete one or more of the items you have selected.',
            description: 'Tooltip when user lacks delete privileges',
          })
        : undefined;

  return (
    <Button
      aria-haspopup="true"
      icon="trash"
      iconPosition="left"
      text={intl.formatMessage({
        id: 'DocumentsTable.deleteButton',
        defaultMessage: 'Delete',
        description: 'Button text for deleting documents',
      })}
      variant="action"
      onClick={() => handleShowModal(MODAL_DELETE, selectedItemsList, 'delete')}
      tooltip={tooltip}
      disabled={disabled}
    />
  );
}

function DocumentGroup(props) {
  const { classes, item } = props;

  if (item.groups && item.groups.length) {
    return (
      <Link
        className={classes.groupIcon}
        to={`/document-group/${item.groups[0].groupId}`}
      >
        <Tooltip
          tooltip={`Group name: ${item.groups[0].name}`}
          id={`documents_row_group_name?name=${item.groups[0].name}`}
          placement="bottom"
        >
          <span
            data-tip
            data-for={`documents_row_group_name?name=${item.groups[0].name}`}
            id={`documents_row_group_name?name=${item.groups[0].name}`}
          >
            <TreeIcon size="18" color={black3} />
            <label className="screenReaderText">
              document group link {item.groups[0].name}
            </label>
          </span>
        </Tooltip>
      </Link>
    );
  } else return null;
}

function DocumentNameType(props) {
  const { classes, item } = props;
  const visibilityLevel =
    item.content_visibility_level || item.inherited_visibility_level;

  return (
    <span className={classes.documentGroupIcon}>
      <span className={classes.documentIcon}>
        {item.type === 'document' ? (
          documentTypeIcon(item.fileType)
        ) : visibilityLevel === 'RESTRICTED' ||
          visibilityLevel === 'NOT_PERMITTED' ? (
          <FolderLockIcon />
        ) : visibilityLevel === 'OPEN_NO_EDIT' ||
          visibilityLevel === 'OPEN_NO_EDIT_DOWNLOAD' ? (
          <FolderViewIcon />
        ) : (
          <FolderIcon size="24" yellow />
        )}
      </span>
      <span className={classes.documentName}>{item.name}</span>
    </span>
  );
}

function Table(props) {
  const intl = useIntl();
  const {
    classes,
    columns,
    data,
    folderItemsCount,
    sortField,
    order,
    handleSortClick,
    handleShowModal,
    handleRowSelect,
    handleOnSelectAllClick,
    isAllSelected,
    selectedItems,
  } = props;

  let autoResetSelectedRowsFlag = false;

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    selectedFlatRows,
    toggleRowSelected,
    state: { columnResizing },
  } = useTable(
    {
      columns,
      data,
      disableMultiSort: true,
      autoResetSelectedRows: autoResetSelectedRowsFlag,
      initialState: { selectedFlatRows: null },
      manualSorting: true,
    },
    useRowSelect,
    useRowState,
    useBlockLayout,
    useResizeColumns,
  );

  useEffect(() => {
    persistTableColumnWidth(columnResizing, 'documents');
  }, [columnResizing]);

  autoResetSelectedRowsFlag =
    selectedFlatRows && selectedFlatRows.length ? false : true;

  const selectedFlatRowsIds = selectedFlatRows
    .map((row) => row.original.id)
    .join(',');
  useEffect(
    () => {
      handleRowSelect(selectedFlatRows);
    },
    [selectedFlatRowsIds], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(
    () => {
      const hasItemsSelected = selectedItems && selectedItems.length > 0;

      if (hasItemsSelected) {
        const ids = selectedItems.map((item) => item.id);
        const selectedRowsData = rows.filter((d) =>
          ids.includes(d.original.id),
        );
        selectedRowsData.forEach((row) => {
          toggleRowSelected(row.id, true);
        });
      }
    },
    [rows, selectedItems], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const forcedMinWidthClass = columns.length < 12 ? 'forcedMinWidthClass' : '';

  function getSortArrow(column) {
    if (sortField === column.id) {
      if (column.id === 'name' || sortField === column.Header) {
        return order === 'desc' ? (
          <Icon icon="arrow-down" label="sort descending" />
        ) : (
          <Icon icon="arrow-up" label="sort ascending" />
        );
      }
    } else {
      return <span aria-label="not sorted" />;
    }
  }

  return (
    <div className={classes.tableWrap}>
      <div {...getTableProps()} className={classes.documentTable}>
        {selectedItems.length ? (
          <div className={forcedMinWidthClass}>
            {headerGroups.map((headerGroup) => {
              return (
                <div
                  {...headerGroup.getHeaderGroupProps()}
                  className={classes.documentTableHeader}
                >
                  {[headerGroup.headers[0]].map((column) => (
                    <div
                      className={classes.documentTableThead}
                      key="selectAllCheckbox"
                    >
                      {column.render('Header')}
                    </div>
                  ))}

                  <div className={classes.theadBulkActionMenu}>
                    <HeaderAddtoGroupAction
                      classes={classes}
                      selectedItemsList={selectedFlatRows}
                      handleShowModal={handleShowModal}
                    />
                    <HeaderMoveAction
                      classes={classes}
                      selectedItemsList={selectedFlatRows}
                      handleShowModal={handleShowModal}
                    />
                    <HeaderCopyAction
                      classes={classes}
                      selectedItemsList={selectedFlatRows}
                      handleShowModal={handleShowModal}
                    />
                    <HeaderEditAction
                      classes={classes}
                      selectedItemsList={selectedFlatRows}
                      handleShowModal={handleShowModal}
                    />
                    <HeaderDeleteAction
                      classes={classes}
                      selectedItemsList={selectedFlatRows}
                      handleShowModal={handleShowModal}
                    />
                    <SelectAllMessage
                      classes={classes}
                      rows={rows}
                      folderItemsCount={folderItemsCount}
                      selectedItemsList={selectedFlatRows}
                      selectedItems={selectedItems}
                      handleOnSelectAllClick={handleOnSelectAllClick}
                      isAllSelected={isAllSelected}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <>
            {headerGroups.map((headerGroup) => (
              <div
                {...headerGroup.getHeaderGroupProps()}
                className={classes.documentTableHeader}
              >
                {headerGroup.headers.map((column) => {
                  if (column.id === 'actionMenu') {
                    return (
                      <span
                        key={column.id}
                        className={classes.actionMenuHeader}
                        role="columnheader"
                      />
                    );
                  } else if (column.id === 'selectAll') {
                    return (
                      <span
                        key={column.id}
                        className={classes.documentTableThead}
                        role="columnheader"
                      >
                        {column.render('Header')}
                      </span>
                    );
                  } else if (column.id === 'cog') {
                    return (
                      <span
                        key={column.id}
                        className={classes.documentTableThead}
                      >
                        {column.render('Header')}
                      </span>
                    );
                  }
                  return (
                    <div key={column.id} className={classes.documentTableThead}>
                      <button
                        {...column.getHeaderProps()}
                        aria-label={`${column.render(
                          'Header',
                        )} sortable column`}
                        className={classes.documentTableTheadButton}
                        onClick={() => handleSortClick(column.id)}
                      >
                        <span className={classes.headerText}>
                          {column.render('Header')}
                        </span>

                        <span className={classes.documentTableSortIcon}>
                          {getSortArrow(column)}
                        </span>
                      </button>
                      <button
                        {...column.getResizerProps()}
                        className={classes.documentTableColumnResizer}
                        title={intl.formatMessage({
                          id: 'DocumentsTable.resizeColumnTooltip',
                          defaultMessage: 'resize column',
                          description: 'Tooltip for column resize handle',
                        })}
                      />
                    </div>
                  );
                })}
              </div>
            ))}
          </>
        )}

        <div {...getTableBodyProps()} className={classes.documentTableRow}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <div {...row.getRowProps()} className={forcedMinWidthClass}>
                {row.cells.map((cell) => {
                  return (
                    <div
                      {...cell.getCellProps()}
                      className={classes.documentTableTd}
                    >
                      {cell.render('Cell')}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function DocumentsTable(props) {
  const intl = useIntl();
  const {
    classes,
    handleOnSelectPageClick,
    handleSelectRowScreenReader,
    onNavigate,
  } = props;

  const hardColumns = [
    {
      id: 'selectAll',
      Header: ({ getToggleAllRowsSelectedProps }) => {
        const toggleProps = { ...getToggleAllRowsSelectedProps() };
        const checkboxChecked = getToggleAllRowsSelectedProps().checked;

        return (
          <div className={classes.documentTableCheckboxContainer}>
            <EcCheckbox
              {...toggleProps}
              checked={checkboxChecked}
              id="selectAllCheckBox"
              onClick={(e) => {
                handleOnSelectPageClick(!checkboxChecked);
                toggleProps.onChange(e);

                const srStatus = intl.formatMessage(
                  {
                    id: 'DocumentsTable.selectAllCheckboxStatus',
                    defaultMessage: 'Select All Rows checkbox {status}',
                    description:
                      'Screen reader status for select all checkbox. Example: status = "checked"',
                  },
                  {
                    status: checkboxChecked
                      ? intl.formatMessage({
                          id: 'DocumentsTable.unchecked2',
                          defaultMessage: 'unchecked',
                          description: 'Checkbox unchecked state',
                        })
                      : intl.formatMessage({
                          id: 'DocumentsTable.checked2',
                          defaultMessage: 'checked',
                          description: 'Checkbox checked state',
                        }),
                  },
                );
                handleSelectRowScreenReader(srStatus);
              }}
            />
            <label htmlFor="selectAllCheckBox" className="screenReaderText">
              {intl.formatMessage(
                {
                  id: 'DocumentsTable.selectAllRowsLabel',
                  defaultMessage: 'Select All Rows {status}',
                  description:
                    'Label for select all rows checkbox. Example: status = "checked"',
                },
                {
                  status: checkboxChecked
                    ? intl.formatMessage({
                        id: 'DocumentsTable.checked',
                        defaultMessage: 'checked',
                        description: 'Checkbox checked state',
                      })
                    : intl.formatMessage({
                        id: 'DocumentsTable.unchecked',
                        defaultMessage: 'unchecked',
                        description: 'Checkbox unchecked state',
                      }),
                },
              )}
            </label>
          </div>
        );
      },
      Cell: ({ row }) => {
        const toggleProps = { ...row.getToggleRowSelectedProps() };
        const checkboxId = row.original.name.replace(/\s/g, '-');
        return (
          <div className={classes.documentTableCheckboxContainer}>
            <EcCheckbox
              id={`selectRow-${checkboxId}`}
              {...toggleProps}
              checked={row.getToggleRowSelectedProps().checked}
              onClick={(e) => {
                toggleProps.onChange(e);
                const isChecked = row.getToggleRowSelectedProps().checked;

                const srStatus = isChecked
                  ? intl.formatMessage(
                      {
                        id: 'DocumentsTable.selectCheckedRow',
                        defaultMessage:
                          'Select the {checkboxId} checkbox checked',
                        description:
                          'Screen reader message for selecting a checkbox that is currently checked. Example: checkboxId = "contract-2024-pdf"',
                      },
                      { checkboxId },
                    )
                  : intl.formatMessage(
                      {
                        id: 'DocumentsTable.selectUncheckedRow',
                        defaultMessage:
                          'Select the {checkboxId} checkbox unchecked',
                        description:
                          'Screen reader message for selecting a checkbox that is currently unchecked. Example: checkboxId = "contract-2024-pdf"',
                      },
                      { checkboxId },
                    );
                handleSelectRowScreenReader(srStatus);
              }}
            />
            <label
              htmlFor={`selectRow-${checkboxId}`}
              className="screenReaderText"
            >
              {intl.formatMessage(
                {
                  id: 'DocumentsTable.selectRowLabel',
                  defaultMessage: 'Select {name}',
                  description:
                    'Label for individual row checkbox. Example: name = "contract.pdf"',
                },
                {
                  name: row.original.name,
                },
              )}
            </label>
          </div>
        );
      },
      width: 40,
    },
    {
      Header: '',
      accessor: 'actionMenu',
      Cell: ({ row }) => {
        return (
          <span className={classes.documentTableTheadActionMenu}>
            {row.original.actionMenu}
          </span>
        );
      },
      width: 50,
    },
    {
      Header: intl.formatMessage({
        id: 'DocumentsTable.nameHeader',
        defaultMessage: 'Name',
        description: 'Name column header',
      }),
      accessor: 'name',
      Cell: ({ row }) => {
        const itemUrl =
          row.original.type === 'document'
            ? `/document/${row.original.id}`
            : `/documents/${row.original.id}`;
        return (
          <Fragment>
            <Tooltip
              id={`tooltip_documents_row_name?${row.original.type}=${row.original.id}`}
              tooltip={intl.formatMessage(
                {
                  id: 'DocumentsTable.nameTooltip',
                  defaultMessage: 'Name: {name}',
                  description:
                    'Tooltip for document/folder name. Example: name = "contract.pdf"',
                },
                {
                  name: row.original.name,
                },
              )}
              placement="bottom"
            >
              <Link
                onClick={() => onNavigate(row.original)}
                data-tip
                data-for={`tooltip_documents_row_name?${row.original.type}=${row.original.id}`}
                aria-label={`${row.original.type} ${row.original.name}`}
                to={itemUrl}
                id={`documents_row_name?${row.original.type}=${row.original.id}`}
                className={classes.folderCell}
              >
                <DocumentNameType classes={classes} item={row.original} />
              </Link>
            </Tooltip>

            <DocumentGroup classes={classes} item={row.original} />
          </Fragment>
        );
      },
      width: getStorageItem('documents_table_name_column_width') || 300,
      minWidth: 180,
      maxWidth: 900,
    },
  ];

  const dynamicColumns = props.columns.map((column, index) => {
    return {
      Header: column.field_name,
      accessor: column.field_name,
      Cell: ({ row }) => {
        const rowItemContent = row.original[index]
          ? Object.values(row.original[index])[0].replace(/[\r\n]/gm, ' ')
          : '';
        return (
          <Fragment>
            <Tooltip
              placemente="left"
              id={rowItemContent}
              tooltip={
                row.original[index] &&
                Object.values(row.original[index])[0].length
                  ? `${column.field_name}: ${rowItemContent}`
                  : undefined
              }
            >
              <span data-tip data-for={rowItemContent}>
                {row.original[index]
                  ? Object.values(row.original[index])
                  : null}
              </span>
            </Tooltip>
          </Fragment>
        );
      },
      width:
        getStorageItem(
          getTableColumnWidthName('documents', column.field_name),
        ) || 150,
      minWidth: 60,
      maxWidth: 400,
    };
  });

  const getColumnSettingMenuItems = () => {
    const items = [];

    items.push({
      id: 'edit_columns_menu_item',
      content: intl.formatMessage({
        id: 'DocumentsTable.editColumnsMenuItem',
        defaultMessage: 'Edit Columns',
        description: 'Menu item for editing table columns',
      }),
      onClick: () => props.handleShowModal(MODAL_DOCUMENTS_COLUMN_FIELDS),
    });

    items.push({
      id: 'save_view_menu_item',
      content: intl.formatMessage({
        id: 'DocumentsTable.saveViewMenuItem',
        defaultMessage: 'Save View',
        description: 'Menu item for saving table view',
      }),
      onClick: () => props.handleShowModal(MODAL_DOCUMENTS_COLUMN_FIELDS_SAVE),
    });

    items.push({
      id: 'load_views_menu_item',
      content: intl.formatMessage({
        id: 'DocumentsTable.loadViewsMenuItem',
        defaultMessage: 'Load Views',
        description: 'Menu item for loading table views',
      }),
      onClick: () =>
        props.handleShowModal(MODAL_DOCUMENTS_COLUMN_FIELDS_VIEW_SWITCH),
    });

    return items;
  };

  const columnsMapped = React.useMemo(
    () => [
      // Let's make a column for selection
      ...hardColumns,
      ...dynamicColumns,

      {
        id: 'cog',
        accessor: '',
        disableSortBy: true,
        Cell: () => <span />,
        Header: () => {
          return (
            <div className={classes.documentTableGearWrapper}>
              <ActionsMenu
                align="end"
                aria-label="Documents column settings"
                title="Documents column settings"
                id="documents_table_columns_settings"
                icon="cog"
                items={getColumnSettingMenuItems()}
              />
            </div>
          );
        },
        sortable: false,
        width: 25,
      },
    ],
    [dynamicColumns, hardColumns, props], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <Table
      classes={classes}
      columns={columnsMapped}
      data={props.data}
      folderItemsCount={props.folderItemsCount}
      sortField={props.sortField}
      order={props.order}
      handleRowSelect={props.handleRowSelect}
      handleSortClick={props.handleSortClick}
      handleShowModal={props.handleShowModal}
      handleOnSelectAllClick={props.handleOnSelectAllClick}
      isAllSelected={props.isAllSelected}
      selectedItems={props.selectedItems}
    />
  );
}

DocumentsTable.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default injectSheet(styles)(DocumentsTable);
